import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Plus, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const Shell = ({ open, onClose, title, children, testId }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="rounded-none sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={testId}>
      <DialogHeader><DialogTitle className="text-slate-800">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const CompanyProfileDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", mission: "", vision: "", targets: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/company").then((r) => setForm({
        name: r.data.name || "", mission: r.data.mission || "",
        vision: r.data.vision || "", targets: (r.data.targets || []).join("\n"),
      })).catch(() => {});
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", {
        name: form.name || undefined, mission: form.mission, vision: form.vision,
        targets: form.targets.split("\n").filter((t) => t.trim()),
      });
      toast.success("Company profile saved");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Shell open={open} onClose={onClose} title="Company profile" testId="setup-company-dialog">
      <div className="space-y-3">
        <div><Label className="text-[11px] uppercase text-slate-500">Company name</Label>
          <Input data-testid="setup-company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" /></div>
        <div><Label className="text-[11px] uppercase text-slate-500">Mission</Label>
          <Textarea data-testid="setup-company-mission" value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} className="rounded-none mt-1" rows={2} /></div>
        <div><Label className="text-[11px] uppercase text-slate-500">Vision</Label>
          <Textarea data-testid="setup-company-vision" value={form.vision} onChange={(e) => setForm({ ...form, vision: e.target.value })} className="rounded-none mt-1" rows={2} /></div>
        <div><Label className="text-[11px] uppercase text-slate-500">Targets (one per line)</Label>
          <Textarea data-testid="setup-company-targets" value={form.targets} onChange={(e) => setForm({ ...form, targets: e.target.value })} className="rounded-none mt-1" rows={3} /></div>
      </div>
      <DialogFooter>
        <Button data-testid="setup-company-save" disabled={saving} onClick={save} className="rounded-none text-white" style={{ background: "var(--brand-primary)" }}>
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
        </Button>
      </DialogFooter>
    </Shell>
  );
};

export const DepartmentsDialog = ({ open, onClose }) => {
  const [depts, setDepts] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (open) api.get("/company").then((r) => setDepts(r.data.departments || [])).catch(() => setDepts([]));
  }, [open]);

  const save = async () => {
    try {
      await api.put("/company", { departments: depts });
      toast.success("Departments saved");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Shell open={open} onClose={onClose} title="Departments" testId="setup-departments-dialog">
      <div className="flex gap-2">
        <Input data-testid="setup-dept-input" value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Operations" className="rounded-none"
          onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { setDepts([...new Set([...depts, draft.trim()])]); setDraft(""); } }} />
        <Button data-testid="setup-dept-add" variant="outline" className="rounded-none"
          onClick={() => { if (draft.trim()) { setDepts([...new Set([...depts, draft.trim()])]); setDraft(""); } }}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 min-h-8">
        {depts.map((d) => (
          <span key={d} className="text-[11px] border border-slate-200 bg-slate-50 px-2 py-1 flex items-center gap-1.5">
            {d}
            <button onClick={() => setDepts(depts.filter((x) => x !== d))} className="text-slate-400 hover:text-red-500">×</button>
          </span>
        ))}
      </div>
      <DialogFooter>
        <Button data-testid="setup-departments-save" onClick={save} disabled={!depts.length}
          className="rounded-none text-white" style={{ background: "var(--brand-primary)" }}>Save</Button>
      </DialogFooter>
    </Shell>
  );
};

export const EmployeesDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", title: "", department: "", email: "", salary_qar: "" });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addOne = async () => {
    setBusy(true);
    try {
      await api.post("/employees", {
        name: form.name, title: form.title, department: form.department,
        email: form.email || undefined,
        salary_qar: form.salary_qar ? Number(form.salary_qar) : undefined,
      });
      toast.success(`${form.name} added`);
      setForm({ name: "", title: "", department: "", email: "", salary_qar: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/employees/import", fd);
      setReport(data);
      toast[data.imported ? "success" : "error"](`Imported ${data.imported} of ${data.total_rows} rows`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    const res = await api.get("/employees/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "employees-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Shell open={open} onClose={onClose} title="Add employees" testId="setup-employees-dialog">
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400">Manual entry</p>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="setup-emp-name" placeholder="Full name *" value={form.name} onChange={set("name")} className="rounded-none" />
          <Input data-testid="setup-emp-title" placeholder="Job title *" value={form.title} onChange={set("title")} className="rounded-none" />
          <Input data-testid="setup-emp-dept" placeholder="Department *" value={form.department} onChange={set("department")} className="rounded-none" />
          <Input data-testid="setup-emp-email" placeholder="Email" value={form.email} onChange={set("email")} className="rounded-none" />
          <Input data-testid="setup-emp-salary" placeholder="Salary (QAR/month)" type="number" value={form.salary_qar} onChange={set("salary_qar")} className="rounded-none" />
          <Button data-testid="setup-emp-add-btn" disabled={busy || !form.name || !form.title || !form.department}
            onClick={addOne} className="rounded-none text-white" style={{ background: "var(--brand-primary)" }}>
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      </div>
      <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400">CSV import</p>
        <div className="flex items-center gap-2">
          <button data-testid="setup-csv-template-btn" onClick={downloadTemplate}
            className="flex items-center gap-1.5 text-[11px] border border-slate-300 px-3 py-2 hover:bg-slate-50">
            <Download className="h-3.5 w-3.5" /> Template
          </button>
          <label className="flex items-center gap-1.5 text-[11px] border border-slate-300 px-3 py-2 cursor-pointer hover:bg-slate-50">
            <Upload className="h-3.5 w-3.5" /> {busy ? "Importing…" : "Upload CSV"}
            <input data-testid="setup-csv-input" type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" />
          </label>
        </div>
        {report && (
          <div className="border border-slate-200 p-3 text-[11px] space-y-1 max-h-40 overflow-y-auto" data-testid="csv-import-report">
            <p className="font-semibold text-slate-700">
              Imported {report.imported}/{report.total_rows} rows
              {report.cap_reached && <span className="text-red-600 ml-1">— plan cap reached</span>}
            </p>
            {report.skipped.map((s) => (
              <p key={s.line} className="text-red-600 font-mono">Line {s.line}: {s.error}</p>
            ))}
            {report.warnings.map((w, i) => (
              <p key={i} className="text-amber-600">{w}</p>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
};

export const OrgLinksDialog = ({ open, onClose }) => {
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (open) api.get("/employees").then((r) => setEmployees(r.data.employees || []));
  }, [open]);

  const setManager = async (id, managerId) => {
    try {
      await api.patch(`/employees/${id}`, { manager_id: managerId });
      setEmployees((es) => es.map((e) => (e.id === id ? { ...e, manager_id: managerId || null } : e)));
      toast.success("Manager updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Shell open={open} onClose={onClose} title="Org relationships — assign managers" testId="setup-org-dialog">
      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
        {employees.map((e) => (
          <div key={e.id} className="flex items-center justify-between py-2 gap-3">
            <div className="min-w-0">
              <p className="text-[12px] font-medium text-slate-700 truncate">{e.name}</p>
              <p className="text-[10px] text-slate-400 truncate">{e.title} · {e.department}</p>
            </div>
            <select data-testid={`manager-select-${e.id}`} value={e.manager_id || ""}
              onChange={(ev) => setManager(e.id, ev.target.value)}
              className="h-8 border border-slate-200 text-[11px] px-2 bg-white max-w-44">
              <option value="">No manager</option>
              {employees.filter((m) => m.id !== e.id).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        ))}
        {!employees.length && <p className="text-[11px] text-slate-400 py-4">Add employees first.</p>}
      </div>
    </Shell>
  );
};
